import { SpaceType } from "@components/atoms/Spacer/types";
import { useAddItemsToCollection, useCollectionList } from "@apis/collection";
import SearchResultModals from "@scenes/searchResults/components/SearchResultModals";
import LoadMoreButton from "@components/atoms/LoadMoreButton";
import { isEmbeddedFrame } from "@utils/iframeMessaging";
import { initializeSearchResultsHeight } from "../../iframe/height/searchResultsHeight";
import { useParentResizesIframe } from "../../hooks/useParentResizesIframe";
import { useFavoritesManager } from "@scenes/searchResults/hooks/useFavoritesManager";
import { useBulkActionsLogic } from "@scenes/searchResults/hooks/useBulkActionsLogic";
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Keyboard,
  Platform,
  View,
  NativeSyntheticEvent,
  NativeScrollEvent,
  DeviceEventEmitter,
  useWindowDimensions,
} from "react-native";
import { Image as ExpoImage } from "expo-image";
import { useQueryClient } from "@tanstack/react-query";
import { Diamond } from "@src/types/DiamondDetail";
import { useSharedValue } from "react-native-reanimated";
import Loader from "@components/atoms/Loader";
import {
  collectSubcategoryCollectionNames,
  getProductListViewData,
  normalizeSavedSearch,
  shouldOpenJewelryVariationModal,
  useChangeViewOptions,
} from "./Utils";
import {
  selectViewTypeForCategory,
  setSearchResultViewType,
  setSearchResultViewTypeInitial,
  useUserInfo,
} from "@redux/slices/userInfoSlice";
import { useTranslation } from "react-i18next";
import { showToast, ToastTypes } from "@components/molecules/VDBToast/util";
import {
  setAuxPayloadData,
  setPayloadData,
  setCollection,
  setSubCollection,
  setSectionName as setSectionNameRedux,
  useSearchFiltersLayout,
  useSearchFiltersPayload,
  useSearchFiltersSlice,
  clearSelectedCollectionOption,
  useSearchFiltersAuxPayload,
  setSectionName as setSectionNameAction,
} from "@redux/slices/searchFilters";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
  setAdWidth,
  setBuilderProduct,
  setLoginModalType,
  setProductType,
  setSearchResultCount,
  setSelectedProduct,
} from "@redux/slices/appStateSlice";
import { useSearchGroupSlice } from "@redux/slices/searchGroupsSlice";
import useHideNavsOnScroll from "@navigations/utils/useHideNavsOnScroll";
import LayoutContainer from "@components/layout/LayoutContainer";
import { useScrollContext } from "@navigations/utils/ScrollContext";
import { ProductCategory, ProductTypeCommon } from "@utils/constants";
import TabletListHeader from "@components/molecules/SearchResultListItem/tabletListHeader";
import ListHeaderComponent from "@scenes/searchResults/components/ListHeaderComponent";
import ListItem from "@scenes/searchResults/components/ListItem";
import EmptyView from "./EmptyView";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useSearchResultsData } from "@scenes/searchResults/hooks/useSearchResultsData";
import {
  getContainerStyle,
  getNumOfColumns,
  getStickyHeaderIndices,
  getToastBottomMargin,
  getVisibleItems,
  isPresentNumOfColumnsFull,
} from "@scenes/searchResults/components/utils";
import { getVisibleColumnKeys } from "@components/molecules/SearchResultListItem/utils";
import HorizontalScrollWrapper from "@scenes/searchResults/components/HorizontalScrollWrapper";
import ListViewSkeleton from "@scenes/searchResults/components/ListViewSkeleton";
import { RootState } from "@redux/store";
import {
  useFocusEffect,
  useIsFocused,
  useNavigation,
} from "@react-navigation/native";
import { PriceModes } from "@components/molecules/PriceModeCard/types";
import { SearchFilterTypeEnum } from "@components/atoms/Filters/utils";
import {
  getSearchParams,
  getSearchParamsJewelry,
} from "@components/molecules/SavedSearchTab/utils/utils";
import { debounce } from "lodash";
import { useCustomSearchData } from "@redux/slices/searchResultsCustomization";
import { useAvailabilityData } from "../../hooks/useAvailabilityData";
import { useDisplayRetailPrice } from "../../hooks/useDisplayRetailPrice";
import { ShareVia } from "@components/molecules/Share/utils/utils";
import {
  ColumnStructure,
  ItemDetailsTypes,
  ModalTypes,
  SearchResultNavigationProps,
} from "./types";
import { useTheme } from "styled-components/native";
import { useSortedTabletListData } from "../../hooks/useSortCustomData";
import { useHeaderBulkOption } from "../../hooks/useHeaderBulkOption";
import Routes from "@navigations/Routes";
import { useProductPrefix } from "../../hooks/useProductPrefix";
import { useUserCurrency } from "../../hooks/getCurrency";
import ListHeaderComponentWeb from "./web-components/ListHeaderComponentWeb";
import { SearchResultProvider } from "@components/molecules/SearchResultListItem/SearchResultContext";
import Breadcrumbs from "@components/molecules/Breadcrumbs/Breadcrumbs";
import { useOrgSettingsSlice } from "@redux/slices/orgSettingsSlice";
import { useJewelryConfiguratorState } from "@scenes/configurator/JewelryConfigurator/Context/JewelryConfiguratorContext";
import { NavigationHistoryState } from "@components/molecules/Breadcrumbs/utils";
import { useNavigationHistory } from "../../hooks/useNavigationHistory";
import { refreshSavedSearchForEntry } from "@scenes/jewelry-builder/utils/utils";
import { FlashList } from "@shopify/flash-list";
import { useMemoryWarningHandler } from "../../hooks/useMemoryWarningHandler";
import useResetFilter from "../../hooks/useResetFilter";
import GridViewSkeleton from "@scenes/searchResults/components/GridViewSkeleton";
import {
  useGetSkeleton,
  useEnableMobileWebView,
  useAllowJewelryVariationModal,
  useAppSettings,
} from "@redux/slices/globalSlice";
import { useLabGrownToast } from "@scenes/explore/hooks/useLabGrownToast";
import { CheckItemAvailabilityResponse } from "@src/types/CartItemListResponse";
import { getProductName } from "@utils/product/productNames";
import { getProductColor } from "@utils/product/productTheme";
import { BulkActions, ShareMenuOptionEnum } from "@utils/product/menuBuilders";
import {
  CartTypeEnum,
  SearchResultsAndFavoritesEnum,
  SearchResultsScreenModals,
} from "@utils/product/searchResultConstants";
import { getProcessedQueryParams } from "@utils/product/queryParams";
import {
  getEffectiveEnterSecondFlow,
  isJewelryProductType,
  stripJewelryLinkageFromPayload,
  JEWELRY_STONE_LINKAGE_KEYS,
} from "./utils/stripJewelryLinkageFromPayload";
import { handleItemClick } from "@utils/product/searchResultsNavigation";
import {
  getStoneTypeFromSectionName,
  StoneType,
} from "@scenes/bulk-request/Utils";
import { usePrivateAppGuard } from "@utils/usePrivateAppGuard";
import { Spacer } from "@components/atoms";
import { useGridMenuOption } from "../../hooks/useGridMenuOption";
import { navigate } from "@navigations/utils/RootNavigation";
import { ScopedCopilotProvider } from "@components/CoachOverlay";

const SearchResultScreen = ({
  route,
  sectionName: sectionNameProp,
  productType: productTypeProp,
  toggleFilterScreen,
  isFilterVisible,
  skipFilterScreenForWeb,
  saved_searches,
  deepLinkNonce,
  collection: collectionFromRoute,
  enterSecondFlow: enterSecondFlowProp,
  showFilter,
  fromNewFilterScreen: fromNewFilterScreenProp = false,
  fromResultScreen = false,
  fromBarcodeScanner: fromBarcodeScannerProp = false,
}: SearchResultNavigationProps) => {
  const navigation = useNavigation();
  const isFocused = useIsFocused();
  const xOffset = useSharedValue(0);
  const userCurrency = useUserCurrency();
  const theme = useTheme();
  const {
    getPriceMode,
    isGuest,
    showBulkRequestFeature,
    allowSearchCoachOverlay,
    hasCustomSorting,
    getProductMarkups,
    getMarkupAmount,
    isBusinessPlan,
    getSeletedViewType,
    isIFrame,
    enableSellReturnButton,
    restrictActionOnUnavailableItems,
    enableShoppingCart,
    showLabGrownPopup,
    getLabGrownText,
  } = useUserInfo();
  const iFrameWithMultipleProducts = useSelector(
    (state: any) =>
      state.globalData?.app_settings?.iFrame_with_multiple_products,
    shallowEqual
  );
  // The iframe grows to fit the list only when the parent page applies the
  // posted height. Old embeds ignore that message and keep the list scroll.
  const parentResizesIframe = useParentResizesIframe();
  const isEmbeddedResults = !!isIFrame() && isEmbeddedFrame();
  const isIframeAutoHeight = isEmbeddedResults && parentResizesIframe;
  const { width: frameWidth } = useWindowDimensions();
  const productType: ProductCategory =
    productTypeProp || route?.params?.productType;
  const priceMode = getPriceMode();
  const isTablet = theme.isTablet || theme.isLargeScreen;
  const searchResultViewTypeInitial = useSelector(
    (state: any) => state.userInfo?.searchResultViewTypeInitial
  );
  const { enableJewelryBuilder } = useOrgSettingsSlice();
  const disableJewelrySettingStoneLinkage = useSelector(
    (state: any) => state.appState?.disableJewelrySettingStoneLinkage
  );
  const sectionNameRedux = useSelector(
    (state: any) => state.searchFilters.sectionName
  );
  const fromNewFilterScreen =
    route?.params?.fromNewFilterScreen || fromNewFilterScreenProp || false;
  const fromBarcodeScanner =
    route?.params?.fromBarcodeScanner || fromBarcodeScannerProp || false;
  const enterSecondFlow = getEffectiveEnterSecondFlow(
    enterSecondFlowProp ?? route?.params?.enterSecondFlow,
    productType,
    disableJewelrySettingStoneLinkage
  );
  const { t } = useTranslation();
  const { bottom } = useSafeAreaInsets();
  const productListViewData = getProductListViewData(productType);
  const selectedViewType = useSelector((state: RootState) =>
    selectViewTypeForCategory(state, productType)
  );
  const [sectionName, setSectionName] = useState(
    sectionNameProp || route?.params?.sectionName
  );
  const defaultListDataOptions = useSortedTabletListData(
    productType,
    priceMode,
    sectionName,
    false
  );
  const addItemsToCollection = useAddItemsToCollection();
  const [singleItem, setSingleItem] = useState<Diamond>();
  const [certificateURL, setCertificateURL] = useState("");
  const [showCertificate, setShowCertificate] = useState(false);
  const auxData = useSearchFiltersAuxPayload();
  const [showModal, setShowModal] = useState<ModalTypes>({
    name: null,
    selectedOptions: null,
    shareMenuOption: null,
    openBulkDialog: false,
    openOrderCartDialog: false,
  });

  const allowJewelryVariationModal = useAllowJewelryVariationModal();
  const [showBMHModal, setShowBMHModal] = useState<boolean>(false);
  const { setItemDetail, resetDefaultSlug, resetVariationList } =
    useJewelryConfiguratorState();

  const { bulkActions: bulkActionsFromHook, requestItems } =
    useHeaderBulkOption(productType, priceMode, t);
  const [listSortedPayload, setListSortedPayload] = useState({});
  const [containerWidth, setContainerWidth] = useState(null);
  const [selectedItems, setSelectedItems] = useState<Diamond[]>([]);
  // Convert selection list -> Set for O(1) lookup in list render.
  const selectedItemIdSet = useMemo(
    () => new Set(selectedItems.map((i) => i?.id).filter(Boolean)),
    [selectedItems]
  );
  const [removeFavoritesItem, setRemoveFavoritesItem] =
    useState<boolean>(false);
  const [sortingFiltersParams, setSortingFiltersParams] = useState<{
    [key: string]: string[];
  }>({}); // Dynamically set values for the given productType

  const [customizeColumnParams, setCustomizeColumnParams] = useState<
    ColumnStructure[]
  >([]);
  const isAllColumnVisible = useMemo(() => {
    return isPresentNumOfColumnsFull(
      productListViewData,
      defaultListDataOptions,
      customizeColumnParams,
      containerWidth
    );
  }, [productType, customizeColumnParams, priceMode, containerWidth]);

  // Ordered list of column "name" keys that the header/row will actually
  // render. Passed to HorizontalScrollWrapper so its totalWidth calculation
  // (and therefore the multiplier) reflects the same set of columns — fixes
  // the trailing blank space that appears with few visible columns.
  const visibleColumnKeys = useMemo(() => {
    const listDataOptions =
      customizeColumnParams && customizeColumnParams.length > 0
        ? customizeColumnParams
        : productListViewData && productListViewData.length > 0
        ? productListViewData
        : defaultListDataOptions;
    return getVisibleColumnKeys(listDataOptions, { sectionName });
  }, [
    customizeColumnParams,
    productListViewData,
    defaultListDataOptions,
    sectionName,
  ]);
  const [sortingMessageShown, setSortingMessageShown] = useState(false);

  const defaultSearchResultProps = {
    mItem: undefined,
    itemIdArray: [],
  };
  const [itemDetails, setItemDetails] = useState<ItemDetailsTypes>(
    defaultSearchResultProps
  );

  const setOpenBulkDialog = (_openBulkDialog: boolean) => {
    setShowModal({
      ...showModal,
      openBulkDialog: _openBulkDialog,
    });
  };
  const setOpenOrderCartDialog = (
    _openOrderCartDialog: boolean,
    isBuyNow: boolean = false,
    targetItem?: Diamond | Diamond[],
    sendQuote: boolean = false
  ) => {
    if (!_openOrderCartDialog) {
      return;
    }
    const items = Array.isArray(targetItem)
      ? targetItem
      : targetItem
      ? [targetItem]
      : selectedItems ?? [];
    setShowModal({
      ...showModal,
      openBulkDialog: false,
      openOrderCartDialog: false,
      isBuyNow: false,
      orderCartItem: null,
    });
    navigate(Routes.OrderNowJewelryVariationScreen, {
      mItem: items[0],
      mItems: items,
      isBuyNow,
      productType,
      ...(sendQuote && { send_quote: true }),
    });
  };
  const useSkeleton = useGetSkeleton();
  const enableMobileWebView = useEnableMobileWebView();
  const { shouldBlock: shouldBlockPublicAccess } = usePrivateAppGuard();
  const getAppSettings = useAppSettings();
  const { triggerLabGrownToast, clearLabGrownToast } = useLabGrownToast({
    productType,
    activeTabKey: sectionName,
    savedSearch:
      route?.params?.saved_search ||
      route?.params?.new_search ||
      saved_searches,
    getAppSettings,
    userInfo: { showLabGrownPopup, getLabGrownText },
    t,
  });

  const useMobileWebView =
    Platform.OS === "web" && theme.isMobile && enableMobileWebView;
  const isMyInventory =
    enableSellReturnButton() &&
    (auxData[sectionName]?.with_available_items === "true" ||
      auxData[sectionName]?.with_available_items === true);

  const dispatch = useDispatch();
  const collectionListCall = useCollectionList(1, 99, "mix_sort");
  const collectionCount =
    collectionListCall?.data?.data?.collections?.length ?? 0;
  const collectionCountRef = useRef(collectionCount);
  const { selectedGroups } = useSearchGroupSlice();
  const { collection, sub_collection } = useSearchFiltersSlice();
  const collectionRef = useRef(collection);
  collectionRef.current = collection;
  const appliedLinkCollectionRef = useRef("");
  const incomingCollection = route?.params?.collection ?? collectionFromRoute;
  const payload = useSearchFiltersPayload(productType);
  const filters = useSearchFiltersLayout();
  const [deepLinkTrigger, setDeepLinkTrigger] = useState(0);
  const filteredFilters =
    filters?.[productType]?.filter(
      (item) =>
        Array.isArray(item.price_modes) &&
        item.price_modes.includes(priceMode ?? PriceModes.WHITE)
    ) ?? [];

  const scrollRef = useRef(null);
  const { jsScrollHandler, scrollToOffset } = useHideNavsOnScroll();
  const [
    scrollValue,
    handleReset,
    ___,
    _____,
    ______,
    _______,
    ________,
    _________,
    searchResultSavedOffset,
  ] = useScrollContext();
  const searchResultCount = useSelector(
    (state: any) => state.appState?.searchResultCount
  );
  const customSearchData = useCustomSearchData();
  const productPrefix = useProductPrefix();
  const showAvailabilityDot = useAvailabilityData(
    customSearchData?.[productType ?? ""]?.[
      `${
        selectedViewType === SearchResultsAndFavoritesEnum.GRID
          ? `grid`
          : `list`
      }_view`
    ]?.availabilty_price_modes ??
      (productType === ProductCategory.JEWELRY
        ? []
        : [PriceModes.GREEN, PriceModes.ORANGE, PriceModes.WHITE])
  );
  const changeViewOptions = useChangeViewOptions(
    customSearchData[productType]?.header?.["sub-sections"]?.change_view
      ?.options || [],
    getPriceMode(),
    t
  );

  const displayRetailPrice = useDisplayRetailPrice(productType, sectionName);

  const bulkActions = useMemo(() => {
    const actions =
      bulkActionsFromHook ??
      BulkActions(t, showBulkRequestFeature(productType, priceMode));

    return actions;
  }, [bulkActionsFromHook, productType, priceMode, t]);

  const menuOptions = useGridMenuOption(
    productType,
    priceMode,
    t,
    customSearchData,
    Routes.SearchResultScreen
  );

  const persistLiveSearchParams = useCallback(() => {
    if (!enableJewelryBuilder) return;
    if (
      disableJewelrySettingStoneLinkage &&
      isJewelryProductType(productType)
    ) {
      return;
    }

    const updated = refreshSavedSearchForEntry(
      {
        screen: route?.name || Routes.SearchResultScreen,
        params: route?.params,
      },
      auxData
    );
    const nextSavedSearch = updated.params?.saved_search;
    if (nextSavedSearch && nextSavedSearch !== route?.params?.saved_search) {
      navigation.setParams({ saved_search: nextSavedSearch } as never);
    }
  }, [
    auxData,
    disableJewelrySettingStoneLinkage,
    enableJewelryBuilder,
    navigation,
    productType,
    route?.params,
    route?.name,
  ]);

  const handleItemClickWrapper = (...props: any) => {
    persistLiveSearchParams();
    setItemDetail({});
    if (
      productType === ProductCategory.JEWELRY ||
      productType === ProductCategory.LABGROWN_JEWELRY
    ) {
      resetDefaultSlug(true);
      resetVariationList();
    }
    handleItemClick(
      ...props,
      enterSecondFlow,
      enableJewelryBuilder,
      handleReset
    );
  };

  const debouncedSetContentWidth = useCallback(
    debounce((localWidth: number) => {
      dispatch(setAdWidth(localWidth));
    }, 300),
    []
  );

  const { addRouteToHistory, clearRouteHistory } = useNavigationHistory();

  const { history } = useSelector(
    (state: any) => state.navigationHistory as NavigationHistoryState
  );
  const historyRef = useRef(history);
  historyRef.current = history;

  const addToHistory = () => {
    const currentHistory = historyRef.current;
    const searchScreenName =
      theme.isLargeScreen || theme.isTablet
        ? Routes.FilterSplitScreen
        : Routes.SearchResultScreen;
    const breadCrumbLabel =
      productType === ProductCategory.JEWELRY
        ? "Setting Search"
        : "Stone Search";
    const alreadyInHistory = currentHistory.some(
      (entry) =>
        entry.params?.productType === productType &&
        entry.params?.breadCrumbLabel === breadCrumbLabel
    );

    // Returning from overlays like bulk-init refocuses this screen. Reuse the
    // existing search crumb instead of appending another "Stone Search".
    if (alreadyInHistory) {
      return;
    }

    let mIndex = -1;
    if (currentHistory.length === 1) {
      const breadCrumb = currentHistory[0].params?.breadCrumbLabel;

      if (
        enableJewelryBuilder &&
        enterSecondFlow === false &&
        (theme.isLargeScreen || theme.isTablet) &&
        (breadCrumb === "Setting Search" || breadCrumb === "Stone Search")
      ) {
        clearRouteHistory();
      }
    } else if (currentHistory.length > 0) {
      mIndex = currentHistory.findIndex(
        (s) =>
          s.screen === searchScreenName && s.params?.productType === productType
      );
    }
    if (
      enableJewelryBuilder &&
      enterSecondFlow === false &&
      (theme.isLargeScreen || theme.isTablet) &&
      mIndex === -1
    ) {
      const params = {
        priceMode,
        productType: productType,
        fromNewFilterScreen: fromResultScreen,
        filterSplitStep: fromResultScreen ? 2 : 1,
        sectionName,
        productPrefix,
        breadCrumbLabel,
        enterSecondFlow: enterSecondFlow,
      };
      addRouteToHistory({ name: searchScreenName, params });
      dispatch(setBuilderProduct(productType));
    }
  };

  const queryClient = useQueryClient();

  useEffect(() => {
    if (!theme.isLargeScreen) {
      return;
    }
    const handleDeepLinkNavigation = () => {
      // Change a state variable so the existing effect executes
      setDeepLinkTrigger((prev) => prev + 1);
    };

    window.addEventListener("deepLinkNavigation", handleDeepLinkNavigation);

    return () => {
      window.removeEventListener(
        "deepLinkNavigation",
        handleDeepLinkNavigation
      );
    };
  }, []);

  useMemoryWarningHandler(() => {
    queryClient
      .getQueryCache()
      .getAll()
      .forEach((query) => {
        if (
          query.queryKey[0] === "SEARCH_RESULTS" &&
          query.getObserversCount() === 0
        ) {
          queryClient.getQueryCache().remove(query);
        }
      });
  });

  useEffect(() => {
    dispatch(setSelectedProduct(getProductColor(productType)));
    dispatch(setProductType(productType));
  }, [productType]);

  const triggerLabGrownToastRef = useRef(triggerLabGrownToast);
  const clearLabGrownToastRef = useRef(clearLabGrownToast);
  const routeParamsRef = useRef(route?.params);
  const lastFocusParamsKeyRef = useRef(JSON.stringify(route?.params ?? {}));
  triggerLabGrownToastRef.current = triggerLabGrownToast;
  clearLabGrownToastRef.current = clearLabGrownToast;
  routeParamsRef.current = route?.params;
  useEffect(() => {
    triggerLabGrownToastRef.current = triggerLabGrownToast;
    clearLabGrownToastRef.current = clearLabGrownToast;
    routeParamsRef.current = route?.params;
  });

  useFocusEffect(
    useCallback(() => {
      const routeSection =
        sectionNameProp || routeParamsRef.current?.sectionName;
      const routeProduct = productType;
      if (routeSection) {
        dispatch(setSectionNameAction(routeSection));
      }
      if (enableJewelryBuilder && routeProduct) {
        dispatch(setProductType(routeProduct));
        dispatch(setBuilderProduct(routeProduct));
      }

      if (!theme.isLargeScreen) {
        const state = navigation.getState?.();
        const currentIndex = state?.index ?? 0;
        const currentRoute = state?.routes?.[currentIndex];
        const currentParams =
          currentRoute?.params ?? routeParamsRef.current ?? {};
        const currentParamsKey = JSON.stringify(currentParams);
        const isParamsChanged =
          currentParamsKey !== lastFocusParamsKeyRef.current;
        lastFocusParamsKeyRef.current = currentParamsKey;

        const prevRoute =
          currentIndex > 0 ? state?.routes?.[currentIndex - 1] : null;
        const arrivedFromFilter =
          prevRoute?.name === Routes.FilterSplitScreen ||
          prevRoute?.name === Routes.FilterScreen;

        if (!arrivedFromFilter || isParamsChanged) {
          triggerLabGrownToastRef.current({
            savedSearch:
              currentParams?.saved_search || currentParams?.new_search,
            activeTabKey: currentParams?.sectionName,
          });
        }
      }
      return () => {
        persistLiveSearchParams();
        clearLabGrownToastRef.current();
      };
    }, [
      theme.isLargeScreen,
      navigation,
      sectionNameProp,
      productType,
      enableJewelryBuilder,
      dispatch,
      persistLiveSearchParams,
    ])
  );

  useEffect(() => {
    if (route?.params?.open_ai_search_widget) {
      DeviceEventEmitter.emit("OPEN_AI_SEARCH_WIDGET");
    }
  }, [route?.params?.open_ai_search_widget]);

  useEffect(() => {
    const fromRoute = sectionNameProp || route?.params?.sectionName;
    let nextSectionName = fromRoute || sectionNameRedux;
    const isJewelry =
      productType === ProductCategory.JEWELRY ||
      productType === ProductCategory.LABGROWN_JEWELRY;
    const isJewelrySection =
      nextSectionName === SearchFilterTypeEnum.JEWELRY ||
      nextSectionName === SearchFilterTypeEnum.LAB_GROWN_JEWELRY;
    // The stone search left in the stack keeps redux on "Single Stones".
    // Adopting that name makes this setting search send the default stone body.
    if (isJewelry && nextSectionName && !isJewelrySection) {
      nextSectionName =
        productType === ProductCategory.LABGROWN_JEWELRY
          ? SearchFilterTypeEnum.LAB_GROWN_JEWELRY
          : SearchFilterTypeEnum.JEWELRY;
    }
    if (nextSectionName && nextSectionName !== sectionName) {
      setSectionName(nextSectionName);
    }
  }, [
    sectionNameRedux,
    sectionNameProp,
    route?.params?.sectionName,
    productType,
    sectionName,
  ]);

  useEffect(() => {
    const unsubscribe = navigation.addListener("beforeRemove", (e) => {
      if (e.data.action.type === "GO_BACK") {
        if (enableJewelryBuilder && enterSecondFlow) {
          if (history?.length > 0) {
            const _product = history[0].params.productType;
            dispatch(setBuilderProduct(_product));
          }
        }
      }
    });

    return unsubscribe;
  }, [navigation]);

  const toggleFilterScreenWrapper = () => {
    // isInitialLayoutLoad.current = true;
    // if (selectedViewType === SearchResultsAndFavoritesEnum.GRID) {
    //   loaderRef.current = true;
    if (toggleFilterScreen && typeof toggleFilterScreen === "function") {
      toggleFilterScreen();
    }
    // setTimeout(() => {
    //   loaderRef.current = false;
    // }, 1000);
    // }
  };

  const jewelrySubType = filteredFilters?.find(
    (filter: any) => filter.name === sectionName
  )?.sub_type;

  const queryCollection = useMemo(() => {
    if (Array.isArray(collection) && collection.length > 0) {
      return collection;
    }
    if (typeof incomingCollection === "string") {
      return incomingCollection
        .split(",")
        .map((c) => c.trim())
        .filter(Boolean);
    }
    return Array.isArray(incomingCollection) ? incomingCollection : [];
  }, [collection, incomingCollection]);

  const subcategoryCollectionNames = useMemo(
    () => collectSubcategoryCollectionNames(filters?.[productType]),
    [filters, productType]
  );

  const linkCollectionNames = useMemo(() => {
    if (typeof incomingCollection === "string") {
      return incomingCollection
        .split(",")
        .map((name) => name.trim())
        .filter(Boolean);
    }
    return Array.isArray(incomingCollection) ? incomingCollection : [];
  }, [incomingCollection]);

  // A pasted link lists the parent and the hoop together. Use the hoop for
  // that first search. After the user picks another collection, keep their
  // choice instead of restoring the link.
  const effectiveSubCollection = useMemo(() => {
    if (Array.isArray(sub_collection) && sub_collection.length > 0) {
      return sub_collection;
    }
    const stillOnLinkCollection =
      linkCollectionNames.length > 0 &&
      (collection.length === 0 ||
        linkCollectionNames.every((name) => collection.includes(name)));
    if (!stillOnLinkCollection || subcategoryCollectionNames.size === 0) {
      return sub_collection;
    }
    const matched = linkCollectionNames.filter((name) =>
      subcategoryCollectionNames.has(name)
    );
    return matched.length > 0 ? matched : sub_collection;
  }, [
    sub_collection,
    subcategoryCollectionNames,
    linkCollectionNames,
    collection,
  ]);

  const queryParams = useMemo(
    () =>
      getProcessedQueryParams({
        productType,
        sortingFiltersParams: sortingFiltersParams?.[productType] || [],
        selectedGroups,
        payload,
        sectionName,
        jewelrySubType,
        filters: filteredFilters,
        collection: queryCollection,
        sub_collection: effectiveSubCollection,
        enterSecondFlow,
        enableJewelryBuilder,
      }),
    [
      productType,
      sortingFiltersParams,
      selectedGroups,
      payload,
      sectionName,
      jewelrySubType,
      filters,
      queryCollection,
      effectiveSubCollection,
      enterSecondFlow,
      enableJewelryBuilder,
    ]
  );

  const updatedQueryParams = useMemo(() => {
    const merged: any = { ...queryParams, ...listSortedPayload };

    const isJewelry =
      productType === ProductCategory.JEWELRY ||
      productType === ProductCategory.LABGROWN_JEWELRY;

    // Change on Review starts a new builder flow with setting search first.
    // Only drop leftover stone linkage. Jewelry types the user just selected
    // must still be sent, or Apply Filters will not change the request.
    if (disableJewelrySettingStoneLinkage && isJewelry) {
      return stripJewelryLinkageFromPayload(merged, JEWELRY_STONE_LINKAGE_KEYS);
    }

    return merged;
  }, [
    queryParams,
    listSortedPayload,
    disableJewelrySettingStoneLinkage,
    productType,
  ]);

  const {
    products,
    promotionsCount,
    fetchNextPage,
    isLoading,
    isFetching: isResultsFetching,
    totalItems,
    isFetchingNextPage,
    hasNextPage,
    serverResponseMessage,
  } = useSearchResultsData(
    productType,
    selectedViewType,
    updatedQueryParams,
    saved_searches,
    isIframeAutoHeight,
    isFocused
  );

  const isFetching = useRef(false);
  const resultsRootRef = useRef<View>(null);
  const [iframeListHeight, setIframeListHeight] = useState(0);
  // Grid and mobile list views are not wrapped in the horizontal table
  // scroller, so FlashList would flex to the iframe and scroll on its own.
  const isIframeGrid =
    isIframeAutoHeight &&
    Platform.OS === "web" &&
    selectedViewType === SearchResultsAndFavoritesEnum.GRID;
  const sizeListToContent =
    isIframeAutoHeight &&
    Platform.OS === "web" &&
    !(
      selectedViewType === SearchResultsAndFavoritesEnum.LIST &&
      (!theme.isMobile || useMobileWebView)
    );
  const onIframeListContentSize = useCallback(
    (_width: number, height: number) => {
      setIframeListHeight((previous) =>
        Math.abs(previous - height) > 1 ? Math.ceil(height) : previous
      );
    },
    []
  );

  useEffect(() => {
    if (
      !isEmbeddedResults ||
      Platform.OS !== "web" ||
      !isFocused ||
      isLoading
    ) {
      return;
    }
    return initializeSearchResultsHeight(
      () => resultsRootRef.current as unknown as HTMLElement | null,
      {
        mode: theme.isLargeScreen ? "web" : theme.isTablet ? "ipad" : "mobile",
        view: selectedViewType,
      }
    );
  }, [
    isEmbeddedResults,
    isIframeAutoHeight,
    isFocused,
    selectedViewType,
    products?.length,
    isLoading,
    isResultsFetching,
    isFetchingNextPage,
    updatedQueryParams,
    theme.isLargeScreen,
    theme.isTablet,
    iframeListHeight,
  ]);

  // Web can trigger `onScroll` very frequently; throttling expensive JS handlers
  // prevents the UI from lagging when results list is large.
  const lastJsScrollUpdateTs = useRef(0);
  const productsRef = useRef(products);
  useEffect(() => {
    productsRef.current = products;
  }, [products]);

  const {
    favoriteFunctionClicked,
    favoriteRemoved,
    updateFavorite,
    isLoading: isFavoritesLoading,
  } = useFavoritesManager({
    productType,
    t,
    theme,
    scrollValue,
    bottom,
    products,
    getToastBottomMargin,
    onShowCollectionModal: (id: number, item: Diamond) => {
      setItemDetails({
        mItem: item,
        itemIdArray: [id],
      });
      if (collectionCountRef.current > 0) {
        setShowModal({
          name: SearchResultsScreenModals.COLLECTIONS_LIST,
          selectedOptions: null,
          shareMenuOption: null,
          openBulkDialog: false,
          openOrderCartDialog: false,
        });
      } else {
        setShowModal({
          name: SearchResultsScreenModals.CREATE_NEW_COLLECTION,
          selectedOptions: null,
          shareMenuOption: null,
          openBulkDialog: false,
          openOrderCartDialog: false,
        });
      }
    },
    onSetRemoveFavoritesItem: setRemoveFavoritesItem,
  });

  const {
    bulkActionCallback,
    addToBulkRequest,
    removeFromBulkRequest,
    processBulkRequest,
    sendEmailExcelData,
    addMultipleItemsToBulk,
    DownloadCSVData,
    isLoading: isBulkActionsLoading,
    checkItemAvailabilityForBulk,
    addToCartMutation,
  } = useBulkActionsLogic({
    productType,
    stoneType:
      getStoneTypeFromSectionName(sectionName) ?? StoneType.SINGLE_STONES,
    priceMode,
    sectionName,
    t,
    scrollValue,
    bottom,
    dispatch,
    isGuest,
    selectedItems,
    itemDetails,
    requestItems,
    singleItem,
    collectionCountRef,
    setShowModal,
    setOpenBulkDialog,
    setOpenOrderCartDialog,
    setShowBMHModal,
    setSingleItem,
    setItemDetails,
    setSelectedItems,
    updateFavorite,
    getToastBottomMargin,
    handleReset,
    setLoginModalType: (type: string) => dispatch(setLoginModalType(type)),
    queryParams: updatedQueryParams,
  });

  const onScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      // Hiding the header changes the list height the iframe is sized to.
      if (fullProductsWithHeader.length === 0 || isIframeAutoHeight) {
        return;
      }
      if (isFocused) {
        if (Platform.OS !== "web") {
          jsScrollHandler(
            event,
            undefined,
            undefined,
            Routes.SearchResultScreen
          );
        } else {
          const now = Date.now();
          // Rough throttle to cut down JS work on web.
          if (now - lastJsScrollUpdateTs.current > 150) {
            lastJsScrollUpdateTs.current = now;
            jsScrollHandler(
              event,
              undefined,
              undefined,
              Routes.SearchResultScreen
            );
          }
        }
      }

      const { contentOffset, layoutMeasurement, contentSize } =
        event.nativeEvent;
      const paddingToBottom = 100;
      if (
        contentOffset.y + layoutMeasurement.height + paddingToBottom >=
        contentSize.height
      ) {
        if (
          !isFetching.current &&
          !isLoading &&
          !isFetchingNextPage &&
          isFocused
        ) {
          isFetching.current = true;
          fetchNextPage().then(() => {
            isFetching.current = false;
          });
        }
      }
    },
    [
      jsScrollHandler,
      fetchNextPage,
      isLoading,
      isFetchingNextPage,
      isFocused,
      isIframeAutoHeight,
    ]
  );

  useResetFilter(() => {
    setListSortedPayload({});
  });

  useEffect(() => {
    if (
      (productType === ProductCategory.JEWELRY ||
        productType === ProductCategory.LABGROWN_JEWELRY) &&
      !isLoading &&
      products?.length - promotionsCount <= 0 &&
      !isFilterVisible
    ) {
      if (showFilter) showFilter();
    }
  }, [productType]);

  useEffect(() => {
    setSortingFiltersParams({});
    setCustomizeColumnParams([]);
  }, [productType]);

  useEffect(() => {
    if (serverResponseMessage && serverResponseMessage?.length > 0) {
      showToast("", t(serverResponseMessage), ToastTypes.INFO, true, true);
    } else if (products?.length > 0 && !sortingMessageShown) {
      if (
        !payload?.show_items_with_images &&
        priceMode !== PriceModes.GREEN &&
        allowSearchCoachOverlay() &&
        productType !== ProductCategory.JEWELRY &&
        selectedViewType === SearchResultsAndFavoritesEnum.GRID
      ) {
        if (!hasCustomSorting(productType)) {
          setSortingMessageShown(true);
          showToast(
            "",
            t("searchResult.itemsWithPicturesTrySorting.text"),
            ToastTypes.INFO,
            true,
            true
          );
        }
      }
    }
  }, [products?.length, sortingMessageShown, serverResponseMessage]);

  useEffect(() => {
    if (products?.length > 0 && selectedItems.length > 0 && !isGuest) {
      setSelectedItems((prevSelected) => {
        const updatedSelected = prevSelected
          .map((item) => {
            const match = products.find((product) => product.id === item.id);
            if (match) {
              return match;
            }
            return null;
          })
          .filter(Boolean);
        return updatedSelected;
      });
    }
  }, [products?.length, !isGuest]);

  const masterCheckboxSelected =
    (products?.length ?? 0) > 0 &&
    selectedItems?.length === products?.length - promotionsCount;

  const onSelectAll = (isChecked: boolean) => {
    if (isChecked) {
      setSelectedItems(
        productsRef.current.filter((item: any) => !item.media_url_url)
      );
    } else {
      setSelectedItems([]);
    }
  };

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage && !isLoading && isFocused) {
      fetchNextPage().then();
    }
  }, [hasNextPage, isFetchingNextPage, isLoading, isFocused]);

  const onSelectViewType = (type: SearchResultsAndFavoritesEnum) => {
    if (theme.isLargeScreen || theme.isTablet) {
      searchResultSavedOffset.value = 0;
    }
    setListSortedPayload({});
    setSortingFiltersParams({});
    dispatch(
      setSearchResultViewType({ category: productType, viewType: type })
    );
    handleReset();
    setSelectedItems([]);
  };

  const isInitialMount = useRef(true);
  const isInitialLayoutLoad = useRef(true);

  useFocusEffect(
    React.useCallback(() => {
      if (isInitialMount.current || theme.isMobile) {
        saveSearchResultCount();
      }

      isInitialMount.current = false;
      isInitialLayoutLoad.current = true;

      return () => {
        if (theme.isMobile) {
          handleReset();
        }
        Keyboard.dismiss();
        setTimeout(() => {
          ExpoImage.clearMemoryCache().catch(() => {});
        }, 300);
      };
    }, [theme.isMobile])
  );

  useFocusEffect(
    useCallback(() => {
      if (isFocused && (theme.isLargeScreen || theme.isTablet)) {
        return scrollToOffset(scrollRef, false);
      }
    }, [theme.isLargeScreen, theme.isTablet, products.length, isFocused])
  );

  useFocusEffect(
    useCallback(() => {
      addToHistory();
    }, [])
  );

  useEffect(() => {
    if (theme.isLargeScreen || theme.isTablet) {
      searchResultSavedOffset.value = 0;
      scrollToOffset(scrollRef, false);
    }
  }, [sortingFiltersParams, selectedViewType, updatedQueryParams, scrollRef]);

  const saveSearchResultCount = () => {
    if (searchResultCount === 0) {
      dispatch(setSearchResultCount(1));
    } else {
      var searchCount = searchResultCount + 1;
      dispatch(setSearchResultCount(searchCount));
    }
  };

  const markup = useMemo(() => {
    if (!getProductMarkups()) {
      return getMarkupAmount();
    }

    switch (productType) {
      case ProductCategory.DIAMOND:
        return getProductMarkups().diamond_markup;
      case ProductCategory.GEMSTONE:
        return getProductMarkups().gemstone_markup;
      case ProductCategory.LAB_GROWN_DIAMOND:
        return getProductMarkups().lab_grown_diamond_markup;
      default:
        return getProductMarkups().jewelry_markup;
    }
  }, [productType]);

  useEffect(() => {
    setSelectedItems([]);
  }, [
    payload,
    sortingFiltersParams,
    selectedGroups[productType],
    priceMode,
    markup,
  ]);

  useEffect(() => {
    if (payload && payload?.[sectionName]?.preference) {
      if (selectedViewType === SearchResultsAndFavoritesEnum.GRID) {
        setSortingFiltersParams((prev: any) => ({
          ...prev,
          [productType]: payload[sectionName].preference,
        }));
        // } else if (selectedViewType === SearchResultsAndFavoritesEnum.LIST) {
        //   const preferenceValue = payload[sectionName].preference[0];
        //   const parts = preferenceValue.trim().split(/\s+/);
        //   const sortField = parts[0];
        //   const sortOrder = parts[1]?.toUpperCase() || "ASC";
        //   setListSortedPayload({
        //     classic_sort: sortField,
        //     classic_sort_order: sortOrder,
        //   });
      }
    }
    if (
      payload &&
      payload?.classic_sort &&
      payload?.classic_sort_order &&
      selectedViewType === SearchResultsAndFavoritesEnum.LIST
    ) {
      setListSortedPayload({
        classic_sort: payload.classic_sort,
        classic_sort_order: payload.classic_sort_order,
      });
    }
  }, [payload]);

  const showDotOnFilter = useCallback(() => {
    if ((theme.isMobile || theme.isTablet) && fromNewFilterScreen) {
      return (
        payload?.[sectionName] && Object.keys(payload[sectionName]).length > 0
      );
    }
    return false;
  }, [payload, fromNewFilterScreen, sectionName]);

  useEffect(() => {
    navigation.setOptions({
      headerTitle: isMyInventory
        ? "My Inventory"
        : jewelrySubType &&
          jewelrySubType === ProductTypeCommon.LABGROWN_JEWELRY_SUB_TYPE
        ? getProductName(ProductCategory.LABGROWN_JEWELRY, t)
        : getProductName(productType, t),
      ...(!iFrameWithMultipleProducts &&
        isIFrame() && {
          headerLeft: () => null,
        }),
    });
    if (searchResultViewTypeInitial[productType] === false) {
      dispatch(
        setSearchResultViewType({
          category: productType,
          viewType:
            changeViewOptions.length === 1 && changeViewOptions?.[0]?.name
              ? changeViewOptions?.[0]?.name
              : getSeletedViewType() || SearchResultsAndFavoritesEnum.GRID,
        })
      );

      dispatch(
        setSearchResultViewTypeInitial({ category: productType, initial: true })
      );
    }
  }, [productType, jewelrySubType, isMyInventory]);

  const fromCollections =
    route?.params?.fromCollections === true ||
    route?.params?.fromCollections === "true";

  useEffect(() => {
    // Selecting from CollectionsScreen already set collection + payload.
    // A leftover default jewelry saved_search (All-Collections) must not
    // overwrite the chosen parent collection on first navigate.
    if (fromCollections) {
      return;
    }

    // The previous stone search stays mounted under Change Jewelry. Its
    // hydration would rewrite the shared section and fire a default search.
    if (!isFocused) {
      return;
    }

    if (
      typeof payload === "object" &&
      payload !== null &&
      (route?.params?.saved_search ||
        route?.params?.new_search ||
        saved_searches)
    ) {
      let params = {};
      let isPair = false;

      try {
        const encodedParams =
          route?.params?.saved_search ||
          route?.params?.new_search ||
          saved_searches ||
          {};
        if (typeof encodedParams === "string") {
          const decodedParams = decodeURI(encodedParams);
          let parsed = JSON.parse(decodedParams.replace(/"|"/g, '"'));

          if (
            productType === ProductCategory.JEWELRY ||
            productType === ProductCategory.LABGROWN_JEWELRY
          ) {
            params = normalizeSavedSearch(JSON.stringify(parsed));
          } else {
            params = parsed;
          }
        } else {
          params = encodedParams;
        }
      } catch (error) {
        console.error("Error decoding or parsing params:", error);
      }

      if (params?.search_options) {
        params = params?.search_options;
      }
      const isSettingSearch =
        productType === ProductCategory.JEWELRY ||
        productType === ProductCategory.LABGROWN_JEWELRY;
      // Apply can hand this screen the previous stone saved search
      // (itemType 1, no jewelry style). Keep the setting results.
      if (
        isSettingSearch &&
        (params as { itemType?: number; jewelry_styles?: unknown })
          ?.itemType === 1 &&
        !(params as { jewelry_styles?: unknown })?.jewelry_styles
      ) {
        return;
      }
      if (
        disableJewelrySettingStoneLinkage &&
        (productType === ProductCategory.JEWELRY ||
          productType === ProductCategory.LABGROWN_JEWELRY)
      ) {
        if (consumedLinkageSavedSearchRef.current) {
          return;
        }
        params = stripJewelryLinkageFromPayload(params);
        consumedLinkageSavedSearchRef.current = true;
      }
      if (params?.pair) {
        isPair = true;
      }
      if (incomingCollection) {
        const savedSearchParams = params as {
          collection_names?: unknown;
          sub_collection?: unknown;
        };
        delete savedSearchParams.collection_names;
        delete savedSearchParams.sub_collection;
      }

      const isJewelryProduct =
        productType === ProductCategory.JEWELRY ||
        productType === ProductCategory.LABGROWN_JEWELRY;
      const routeSectionName =
        sectionNameProp ||
        route?.params?.sectionName ||
        sectionNameRedux ||
        sectionName;
      const savedSubType = (params as { sub_type?: string })?.sub_type;
      const isLabGrownSection =
        savedSubType === ProductTypeCommon.LABGROWN_JEWELRY_SUB_TYPE ||
        productType === ProductCategory.LABGROWN_JEWELRY ||
        routeSectionName === SearchFilterTypeEnum.LAB_GROWN_JEWELRY;
      let mSectionName = isJewelryProduct
        ? isLabGrownSection
          ? SearchFilterTypeEnum.LAB_GROWN_JEWELRY
          : routeSectionName || SearchFilterTypeEnum.JEWELRY
        : routeSectionName;
      if (
        isJewelryProduct &&
        mSectionName !== SearchFilterTypeEnum.JEWELRY &&
        mSectionName !== SearchFilterTypeEnum.LAB_GROWN_JEWELRY
      ) {
        mSectionName = isLabGrownSection
          ? SearchFilterTypeEnum.LAB_GROWN_JEWELRY
          : SearchFilterTypeEnum.JEWELRY;
      }

      setSectionName(mSectionName);
      if (mSectionName) {
        dispatch(setSectionNameRedux(mSectionName));
      }

      let newPayload = Object.keys(params || [])?.reduce((acc, key) => {
        acc[key] = params[key];
        return acc;
      }, {});

      if (Object.keys(newPayload).length > 0) {
        if (mSectionName) {
          if (
            mSectionName === ProductCategory.JEWELRY ||
            mSectionName === ProductCategory.LABGROWN_JEWELRY ||
            mSectionName === SearchFilterTypeEnum.JEWELRY ||
            mSectionName === SearchFilterTypeEnum.LAB_GROWN_JEWELRY
          ) {
            newPayload = {
              [mSectionName]: getSearchParamsJewelry(params, dispatch, true),
            };
          } else newPayload = { [mSectionName]: getSearchParams(newPayload) };
        } else {
          if (productType === ProductCategory.JEWELRY) {
            newPayload = {
              [SearchFilterTypeEnum.JEWELRY]: getSearchParamsJewelry(
                newPayload,
                dispatch,
                true
              ),
            };
            setSectionName(SearchFilterTypeEnum.JEWELRY);
          } else if (productType === ProductCategory.LABGROWN_JEWELRY) {
            newPayload = {
              [SearchFilterTypeEnum.LAB_GROWN_JEWELRY]: getSearchParamsJewelry(
                newPayload,
                dispatch,
                true
              ),
            };
            setSectionName(SearchFilterTypeEnum.LAB_GROWN_JEWELRY);
          } else if (isPair) {
            newPayload = {
              [SearchFilterTypeEnum.PAIRS]: getSearchParams(newPayload),
            };
            setSectionName(SearchFilterTypeEnum.PAIRS);
          } else {
            newPayload = {
              [SearchFilterTypeEnum.SINGLE_STONES]: getSearchParams(newPayload),
            };
            setSectionName(SearchFilterTypeEnum.SINGLE_STONES);
          }
        }
        dispatch(setPayloadData({ data: { ...newPayload }, productType }));
        dispatch(setAuxPayloadData(newPayload));
      }
    }
  }, [
    route?.params?.saved_search,
    route?.params?.new_search,
    saved_searches,
    deepLinkTrigger,
    fromCollections,
    deepLinkNonce,
    isFocused,
  ]);

  useEffect(() => {
    let collectionParam = incomingCollection;
    let collectionSource: "route" | "url" | "none" = collectionParam
      ? "route"
      : "none";

    if (Platform.OS === "web" && !collectionParam) {
      const searchParams = new URLSearchParams(window.location.search);
      collectionParam = searchParams.get("collection");
      if (collectionParam) collectionSource = "url";
    }

    if (collectionParam) {
      let collectionsToSet: string[] = [];

      if (typeof collectionParam === "string") {
        collectionsToSet = collectionParam
          .split(",")
          .map((c) => c.trim())
          .filter((c) => c);
      } else if (Array.isArray(collectionParam)) {
        collectionsToSet = collectionParam;
      }

      if (collectionsToSet.length > 0) {
        // Keep subcategory selections when the route still only lists the
        // parent. Do not depend on redux collection, or switching away from
        // the explore collection (All-Jewelry → Rings) is overwritten.
        const reduxCollection = collectionRef.current ?? [];
        const alreadyHydrated = collectionsToSet.every((c) =>
          reduxCollection.includes(c)
        );
        // Same stale-URL path as FilterScreen: ?collection= still names the
        // explore parent after the dropdown changes it.
        const staleUrlCollection =
          collectionSource === "url" &&
          reduxCollection.length > 0 &&
          !alreadyHydrated;
        const linkKey = collectionsToSet.join("\u0000");
        // Apply the link once. Choosing Rings or Earrings later clears the
        // subcategory, and repeating this write would put the link back.
        if (
          appliedLinkCollectionRef.current === linkKey ||
          alreadyHydrated ||
          staleUrlCollection
        ) {
          appliedLinkCollectionRef.current = linkKey;
          return;
        }
        appliedLinkCollectionRef.current = linkKey;
        dispatch(clearSelectedCollectionOption());
        dispatch(setCollection(collectionsToSet));
        const urlSubcategories = collectionsToSet.filter((name) =>
          subcategoryCollectionNames.has(name)
        );
        if (urlSubcategories.length > 0) {
          dispatch(setSubCollection(urlSubcategories));
        }
      }
    }
  }, [incomingCollection, dispatch, subcategoryCollectionNames]);

  const renderListHeaderComponent = useCallback(() => {
    return selectedViewType === SearchResultsAndFavoritesEnum.LIST &&
      (totalItems || isLoading) &&
      (isTablet || useMobileWebView) ? (
      <View
        style={{
          backgroundColor: theme["core-02"],
          // Only need overflow:hidden for large screen (transform-based scrolling)
          ...(theme.isLargeScreen && { overflow: "hidden" }),
          ...(useMobileWebView && {
            paddingTop: SpaceType.Basic,
          }),
        }}
      >
        <TabletListHeader
          mItem={itemDetails?.mItem}
          checkBoxSelection={onSelectAll}
          ismItemChecked={masterCheckboxSelected}
          listMargin={17}
          productType={productType}
          onSortChange={(sortingPayload) =>
            setListSortedPayload(sortingPayload)
          }
          showCheckbox={true}
          showFavorite={true}
          fromBulkList={false}
          customizeColumnParams={customizeColumnParams}
          sectionName={sectionName}
          useMobileWebView={useMobileWebView}
        />
      </View>
    ) : null;
  }, [
    selectedViewType,
    totalItems,
    isLoading,
    itemDetails,
    masterCheckboxSelected,
    productType,
    customizeColumnParams,
    useMobileWebView,
  ]);

  const checkBoxSelectionUpdate = (checked: boolean, item: Diamond) => {
    if (checked) {
      setSelectedItems((old) => [...old, item]);
    } else {
      setSelectedItems((old) => old.filter((_item) => _item.id !== item.id));
    }
  };

  const menuOptionSelected = (eventName: string, item?: Diamond) => {
    if (!item) return;

    const itemId = item.id ?? 0;

    switch (eventName) {
      case SearchResultsAndFavoritesEnum.ADD_TO_BULK_REQUEST:
        if (isGuest) {
          setOpenBulkDialog(false);
          dispatch(setLoginModalType("default"));
        } else if (itemId) {
          setShowModal({
            name: SearchResultsScreenModals.LOADER_MODAL,
            selectedOptions: null,
            shareMenuOption: null,
            openBulkDialog: false,
            openOrderCartDialog: false,
          });
          if (restrictActionOnUnavailableItems()) {
            checkItemAvailabilityForBulk(
              [itemId],
              (data: CheckItemAvailabilityResponse) => {
                if (data?.status_code === 1) {
                  addToBulkRequest(itemId, CartTypeEnum.BULK_REQUEST_TYPE);
                }
              }
            );
          } else {
            addToBulkRequest(itemId, CartTypeEnum.BULK_REQUEST_TYPE);
          }
        }
        break;

      case SearchResultsAndFavoritesEnum.REMOVE_FROM_BULK_REQUEST:
        if (isGuest) {
          setOpenBulkDialog(false);
          dispatch(setLoginModalType("default"));
        } else if (itemId) {
          setShowModal({
            name: SearchResultsScreenModals.LOADER_MODAL,
            selectedOptions: null,
            shareMenuOption: null,
            openBulkDialog: false,
            openOrderCartDialog: false,
          });
          removeFromBulkRequest(itemId, CartTypeEnum.BULK_REQUEST_TYPE);
        }
        break;
      case SearchResultsAndFavoritesEnum.VIEW_DETAILS:
        resetVariationList();
        handleItemClick(
          item,
          productType,
          priceMode,
          productPrefix,
          addRouteToHistory,
          enterSecondFlow,
          enableJewelryBuilder,
          handleReset
        );
        break;

      case SearchResultsAndFavoritesEnum.SHARE_EMAIL:
        if (isGuest) {
          setOpenBulkDialog(false);
          dispatch(setLoginModalType("default"));
        } else {
          setItemDetails({
            mItem: item,
            itemIdArray: [itemId],
          });
          setShowModal({
            name: SearchResultsScreenModals.SHARE,
            selectedOptions: ShareVia.EMAIL,
            shareMenuOption: ShareMenuOptionEnum.SINGLE,
            openBulkDialog: false,
            openOrderCartDialog: false,
          });
        }
        break;
      case SearchResultsAndFavoritesEnum.SHARE_NATIVE:
        if (isGuest) {
          setOpenBulkDialog(false);
          dispatch(setLoginModalType("default"));
        } else {
          setItemDetails({
            mItem: item,
            itemIdArray: [itemId],
          });
          setShowModal({
            name: SearchResultsScreenModals.SHARE,
            selectedOptions: ShareVia.NATIVE,
            shareMenuOption: ShareMenuOptionEnum.SINGLE,
            openBulkDialog: false,
            openOrderCartDialog: false,
          });
        }
        break;
      case SearchResultsAndFavoritesEnum.ADD_TO_FAVORITES:
        favoriteFunctionClicked(false, item);
        break;

      case SearchResultsAndFavoritesEnum.REMOVE_FROM_FAVORITES:
        favoriteFunctionClicked(true, item);
        break;
      case SearchResultsAndFavoritesEnum.REQUEST_ITEMS:
        if (isGuest) {
          setOpenBulkDialog(false);
          dispatch(setLoginModalType("default"));
        } else {
          setShowModal({
            name: null,
            selectedOptions: null,
            shareMenuOption: null,
            openBulkDialog: false,
            openOrderCartDialog: false,
          });

          setSingleItem(item);

          setShowBMHModal(true);
        }
        break;
      case SearchResultsAndFavoritesEnum.BUY_ITEMS:
      case SearchResultsAndFavoritesEnum.MEMO_ITEMS:
      case SearchResultsAndFavoritesEnum.HOLD_ITEMS:
        if (isGuest) {
          setOpenBulkDialog(false);
          dispatch(setLoginModalType("default"));
        } else {
          setShowBMHModal(false);
          processBulkRequest(eventName, item);
        }
        break;
      case SearchResultsAndFavoritesEnum.ADD_TO_COLLECTION:
        if (isGuest) {
          setOpenBulkDialog(false);
          dispatch(setLoginModalType("default"));
        } else {
          setItemDetails({
            mItem: item,
            itemIdArray: [itemId],
          });
          if (collectionCountRef.current > 0) {
            setShowModal({
              name: SearchResultsScreenModals.COLLECTIONS_LIST,
              selectedOptions: null,
              shareMenuOption: null,
              openBulkDialog: false,
              openOrderCartDialog: false,
            });
          } else {
            setShowModal({
              name: SearchResultsScreenModals.CREATE_NEW_COLLECTION,
              selectedOptions: null,
              shareMenuOption: null,
              openBulkDialog: false,
              openOrderCartDialog: false,
            });
          }
        }
        break;
      case SearchResultsAndFavoritesEnum.ADD_TO_BULK_LIST:
        setShowModal({
          name: SearchResultsScreenModals.LOADER_MODAL,
          selectedOptions: null,
          shareMenuOption: null,
          openBulkDialog: false,
          openOrderCartDialog: false,
        });
        const bulkItemIds =
          selectedItems.length > 0
            ? selectedItems.map((_Item) => Number(_Item?.id ?? 0))
            : [itemId];
        addMultipleItemsToBulk(bulkItemIds, undefined, false);
        break;
      case SearchResultsAndFavoritesEnum.DOWNLOAD_CSV_PDF:
        if (isGuest) {
          setOpenBulkDialog(false);
          dispatch(setLoginModalType("default"));
        } else {
          setShowModal({
            name: SearchResultsScreenModals.DOWNLOAD_CSV_PDF,
            selectedOptions: null,
            shareMenuOption: null,
            openBulkDialog: false,
            openOrderCartDialog: false,
          });
        }
        break;
      case SearchResultsAndFavoritesEnum.ADD_TO_CART:
      case SearchResultsAndFavoritesEnum.BUY_NOW:
      case SearchResultsAndFavoritesEnum.SEND_QUOTE: {
        const isSendQuote =
          eventName === SearchResultsAndFavoritesEnum.SEND_QUOTE;
        const isBuyNow =
          eventName === SearchResultsAndFavoritesEnum.BUY_NOW || isSendQuote;
        if (isGuest) {
          setOpenBulkDialog(false);
          dispatch(setLoginModalType("default"));
        } else {
          if (
            shouldOpenJewelryVariationModal(
              allowJewelryVariationModal,
              item,
              productType
            )
          ) {
            setOpenOrderCartDialog(true, isBuyNow, item, isSendQuote);
          } else {
            setShowModal({
              name: SearchResultsScreenModals.LOADER_MODAL,
              selectedOptions: null,
              shareMenuOption: null,
              openBulkDialog: false,
              openOrderCartDialog: false,
            });
            const cartType = enableShoppingCart()
              ? CartTypeEnum.SHOPPING_CART_TYPE
              : CartTypeEnum.ORDER_CART;
            addToCartMutation(item, cartType, productType, isBuyNow);
          }
        }
        break;
      }
      case SearchResultsAndFavoritesEnum.SHARE_VIA_LINK:
        if (isGuest) {
          setOpenBulkDialog(false);
          dispatch(setLoginModalType("default"));
        } else {
          setItemDetails({
            mItem: item,
            itemIdArray: [itemId],
          });
          setShowModal({
            name: SearchResultsScreenModals.SHARE,
            selectedOptions: ShareVia.LINK,
            shareMenuOption: ShareMenuOptionEnum.SINGLE,
            openBulkDialog: false,
            openOrderCartDialog: false,
          });
        }
        break;
    }
  };

  useEffect(() => {
    collectionCountRef.current = collectionCount;
  }, [collectionCount]);

  const hadSectionPayloadRef = useRef(false);

  useEffect(() => {
    if (Platform.OS !== "web") return;

    const sectionPayload = payload?.[sectionName];
    const isPayloadEmpty =
      !sectionPayload ||
      typeof sectionPayload !== "object" ||
      Object.keys(sectionPayload).length === 0;
    const urlSavedSearch = new URLSearchParams(window.location.search).get(
      "saved_search"
    );
    const existingSavedSearch =
      route?.params?.saved_search || saved_searches || urlSavedSearch;

    if (isPayloadEmpty) {
      if (!hadSectionPayloadRef.current || !existingSavedSearch) return;
      hadSectionPayloadRef.current = false;
      navigation.setParams({
        saved_search: undefined,
      } as never);
      return;
    }

    hadSectionPayloadRef.current = true;
    if (existingSavedSearch) return;

    navigation.setParams({
      saved_search: JSON.stringify(sectionPayload),
    } as never);
  }, [
    payload,
    sectionName,
    route?.params?.saved_search,
    saved_searches,
    navigation,
  ]);

  const addCollectionSuccess = (data: any) => {
    showToast(
      t("global.text.done"),
      t("favorites.newCollectionCreated.text"),
      ToastTypes.SUCCESS,
      true,
      true
    );
    collectionListCall.refetch().then();
    addItemToCollection(data?.id);
  };

  const addItemToCollection = (collectionId: number) => {
    const collectionIdList = [collectionId];
    const selectedItemArray = selectedItems.map((item) => item?.id ?? 0);
    const itemIdList = itemDetails?.mItem?.id
      ? [itemDetails?.mItem?.id]
      : selectedItemArray;

    const jsonObject = {
      stock_item_id: itemIdList,
      collection_id: collectionIdList,
    };

    addItemsToCollection.mutate(
      {
        collection: jsonObject,
      },
      {
        onSuccess(data: any) {
          showToast(
            data?.data?.title,
            data?.data?.message,
            ToastTypes.SUCCESS,
            true,
            true,
            getToastBottomMargin(scrollValue, bottom)
          );
        },
      }
    );
  };

  const safeContainerWidth = containerWidth ?? 0;

  const numColumns = useMemo(
    () =>
      getNumOfColumns(
        selectedViewType,
        safeContainerWidth,
        theme,
        isFilterVisible
      ),
    [selectedViewType, safeContainerWidth, theme, isFilterVisible]
  );

  // Remount FlashList when the search itself changes (deeplink / saved search),
  // not when the current result set is reordered by sorting.
  const flashListInstanceKey = useMemo(() => {
    const saved = saved_searches ?? route?.params?.saved_search;
    const savedKey =
      saved == null || saved === ""
        ? "default"
        : typeof saved === "string"
        ? saved
        : JSON.stringify(saved);
    return `${numColumns}-${savedKey}`;
  }, [numColumns, saved_searches, route?.params?.saved_search]);

  const fullProducts = useMemo(() => {
    const productsCopy = [...products];
    const remainder = products.length % numColumns;

    if (remainder !== 0) {
      for (let i = 0; i < numColumns - remainder; i++) {
        productsCopy.push({});
      }
    }

    return productsCopy;
  }, [products, numColumns]);

  const containerStyle = useMemo(
    () => ({
      ...(getContainerStyle(selectedViewType, theme) as object),
      // Load More sits right below the results in the iframe, so the bottom
      // padding reserved for native bars only shows up as an empty gap.
      ...(isIframeAutoHeight && {
        flexGrow: 0,
        paddingBottom:
          selectedViewType === SearchResultsAndFavoritesEnum.GRID
            ? SpaceType.Big
            : 0,
      }),
    }),
    [selectedViewType, theme, isIframeAutoHeight]
  );

  const fullProductsWithHeader = useMemo(() => {
    const shouldAddHeader =
      selectedViewType === SearchResultsAndFavoritesEnum.LIST &&
      (theme.isLargeScreen || theme.isTablet || useMobileWebView) &&
      (totalItems > 0 || isLoading);

    return shouldAddHeader
      ? [{ __isHeader: true }, ...fullProducts]
      : fullProducts;
  }, [
    fullProducts,
    selectedViewType,
    theme.isLargeScreen,
    theme.isTablet,
    totalItems,
    isLoading,
    useMobileWebView,
  ]);

  const renderListView = useCallback(
    ({ item, index }: any) => {
      if (
        item.__isHeader &&
        selectedViewType === SearchResultsAndFavoritesEnum.LIST &&
        (theme.isLargeScreen || theme.isTablet || useMobileWebView)
      ) {
        return renderListHeaderComponent();
      } else {
        return (
          <ListItem
            checkBoxSelectionUpdate={checkBoxSelectionUpdate}
            favoriteFunctionClicked={favoriteFunctionClicked}
            handleItemClick={handleItemClickWrapper}
            menuOptionSelected={menuOptionSelected}
            menuOptions={menuOptions}
            productType={productType}
            index={index - 1}
            item={item}
            selectedViewType={selectedViewType}
            isSelected={selectedItemIdSet.has(item?.id)}
            isFavorite={item?.is_favorite}
            columnCount={numColumns}
            isFilterVisible={isFilterVisible}
            isBusinessPlan={isBusinessPlan()}
            showAvailabilityDot={showAvailabilityDot}
            inCart={item.in_cart}
            displayRetailPrice={displayRetailPrice}
            collectionCount={collectionCount}
            customizeColumnParams={customizeColumnParams}
            isAllColumnVisible={isAllColumnVisible}
            userCurrency={userCurrency}
            productPrefix={productPrefix}
            handleReset={handleReset}
            sectionName={sectionName}
            handleLinkClick={handleLinkClick}
            xOffset={xOffset}
          />
        );
      }
    },
    [
      productType,
      selectedViewType,
      numColumns,
      isFilterVisible,
      showAvailabilityDot,
      displayRetailPrice,
      collectionCount,
      customizeColumnParams,
      isAllColumnVisible,
      userCurrency,
      productPrefix,
      selectedItems,
      useMobileWebView,
    ]
  );

  const keyExtractor = (item: any, index: number) => {
    if (
      item.__isHeader &&
      selectedViewType === SearchResultsAndFavoritesEnum.LIST &&
      (theme.isLargeScreen || theme.isTablet || useMobileWebView)
    ) {
      return "header";
    }
    if (!item.id) {
      return `empty-${index}`;
    }
    if (item?.promotion_index) {
      return `ad-${index}-${item?.id}`;
    }

    return `${index}-${item?.id}`;
  };

  const handleLinkClick = (item: Diamond, key: string) => {
    setSingleItem(item);
    setCertificateURL(item?.[key] ?? item?.cert_url);
    setShowCertificate(!showCertificate);
  };

  if (
    isLoading &&
    !useSkeleton &&
    (selectedViewType !== SearchResultsAndFavoritesEnum.LIST || theme.isMobile)
  )
    return <Loader />;

  const setContentWidth = (e: any) => {
    const { width: layoutWidth } = e.nativeEvent.layout;
    if (layoutWidth !== 0) {
      if (containerWidth !== layoutWidth) {
        setContainerWidth(layoutWidth);
      }

      debouncedSetContentWidth(layoutWidth);

      if (isInitialLayoutLoad.current) {
        isInitialLayoutLoad.current = false;
      }
    }
  };

  if (shouldBlockPublicAccess) {
    return <View />;
  }

  return (
    <ScopedCopilotProvider scope="SearchResult">
      <SearchResultProvider>
        {!isLoading &&
        products?.length - promotionsCount <= 0 &&
        !theme.isLargeScreen &&
        !useMobileWebView ? (
          <EmptyView
            productType={productType}
            sectionName={sectionName}
            isFilterVisible={isFilterVisible}
          />
        ) : (
          <View
            ref={resultsRootRef}
            style={{
              ...(selectedViewType !== SearchResultsAndFavoritesEnum.LIST &&
                theme.isLargeScreen && {
                  maxWidth: theme.isLargeScreen
                    ? 1552 + (!isFilterVisible ? 320 : 0)
                    : 608,
                }),
              width: "100%",
              flex: 1,
              alignSelf: "center",
              backgroundColor: theme["core-02"],
            }}
            onLayout={setContentWidth}
          >
            <LayoutContainer allowFlex={false}>
              {theme.isMobile && enableJewelryBuilder && <Breadcrumbs />}
              {!theme.isLargeScreen ? (
                <ListHeaderComponent
                  sectionName={sectionName}
                  selectedItems={selectedItems}
                  selectedViewType={selectedViewType}
                  selectViewType={onSelectViewType}
                  sortingFiltersParams={sortingFiltersParams[productType]}
                  productType={productType}
                  totalItems={totalItems}
                  totalItemsPages={products?.length - promotionsCount}
                  masterCheckBoxSelection={onSelectAll}
                  searchResultContainerWidth={safeContainerWidth}
                  setSortingFiltersParams={setSortingFiltersParams}
                  toggleFilterScreen={toggleFilterScreenWrapper}
                  queryParamsList={queryParams}
                  masterCheckboxSelected={masterCheckboxSelected}
                  isFilterVisible={isFilterVisible}
                  showDotOnFilter={showDotOnFilter()}
                  setCustomizeColumnParams={setCustomizeColumnParams}
                  enterSecondFlow={enterSecondFlow}
                  fromBarcodeScanner={fromBarcodeScanner}
                  floatingButtonClick={() => setOpenBulkDialog(true)}
                  products={products}
                  isIFrame={isIFrame}
                  enableMobileWebView={useMobileWebView}
                />
              ) : (
                <ListHeaderComponentWeb
                  sectionName={sectionName}
                  selectedItems={selectedItems}
                  selectedViewType={selectedViewType}
                  selectViewType={onSelectViewType}
                  sortingFiltersParams={sortingFiltersParams[productType]}
                  productType={productType}
                  totalItems={totalItems}
                  totalItemsPages={products?.length - promotionsCount}
                  masterCheckBoxSelection={onSelectAll}
                  searchResultContainerWidth={safeContainerWidth}
                  setSortingFiltersParams={setSortingFiltersParams}
                  toggleFilterScreen={toggleFilterScreenWrapper}
                  queryParamsList={queryParams}
                  masterCheckboxSelected={masterCheckboxSelected}
                  isFilterVisible={isFilterVisible}
                  showDotOnFilter={showDotOnFilter()}
                  handleOnClick={() => setOpenBulkDialog(true)}
                  selectOption={bulkActionCallback}
                  skipFilterScreenForWeb={skipFilterScreenForWeb}
                  setCustomizeColumnParams={setCustomizeColumnParams}
                  isloading={isLoading}
                  enterSecondFlow={enterSecondFlow}
                  fromBarcodeScanner={fromBarcodeScanner}
                />
              )}
              <HorizontalScrollWrapper
                isListView={
                  selectedViewType === SearchResultsAndFavoritesEnum.LIST
                }
                isTablet={isTablet}
                xOffset={xOffset}
                marginHorizontal={
                  useMobileWebView ? SpaceType.Extra : SpaceType.Big
                }
                totalItems={totalItems}
                isAllColumnVisible={isAllColumnVisible}
                hiddenColumns={getVisibleItems(productListViewData)}
                visibleColumnKeys={visibleColumnKeys}
                showCheckbox={true}
                showFavorite={true}
                isLoading={isLoading}
                isFilterVisible={isFilterVisible}
                priceMode={priceMode}
                productType={productType}
                fromSearchResults={true}
                sectionName={sectionName}
                useMobileWebView={useMobileWebView}
              >
                {containerWidth && (
                  <FlashList
                    key={flashListInstanceKey}
                    keyExtractor={keyExtractor}
                    extraData={selectedItems}
                    estimatedItemSize={
                      selectedViewType === SearchResultsAndFavoritesEnum.GRID
                        ? 280
                        : 120
                    }
                    drawDistance={Platform.OS === "web" ? 900 : 250}
                    {...(Platform.OS === "web" && {
                      initialDrawBatchSize: 24,
                    })}
                    stickyHeaderIndices={getStickyHeaderIndices(
                      selectedViewType,
                      isTablet || useMobileWebView
                    )}
                    {...((theme.isLargeScreen ||
                      (Platform.OS === "android" && theme.isMobile)) &&
                      selectedViewType ===
                        SearchResultsAndFavoritesEnum.GRID && {
                        removeClippedSubviews: true,
                      })}
                    ref={scrollRef}
                    onScroll={onScroll}
                    data={fullProductsWithHeader}
                    renderItem={renderListView}
                    contentContainerStyle={containerStyle}
                    {...(sizeListToContent && {
                      onContentSizeChange: onIframeListContentSize,
                      style: {
                        flexGrow: 0,
                        flexShrink: 0,
                        flexBasis: "auto",
                        ...(iframeListHeight > 0 && {
                          // Grid content lands a few pixels past the viewport,
                          // which is enough to keep the list's own scroll.
                          height: iframeListHeight + (isIframeGrid ? 12 : 0),
                        }),
                      },
                    })}
                    numColumns={numColumns}
                    // A list as tall as its content always looks scrolled
                    // to the end and would fetch every page.
                    onEndReached={
                      isIframeAutoHeight ? undefined : handleLoadMore
                    }
                    onEndReachedThreshold={0.66}
                    scrollEnabled={!isLoading}
                    {...(isIframeGrid && {
                      // scrollEnabled={false} sets touch-action:none, so a
                      // swipe on the cards never reaches the parent page.
                      // Keep the list from being its own scroller instead.
                      overrideProps: {
                        style: {
                          overflow: "visible",
                          touchAction: "pan-y",
                          WebkitOverflowScrolling: "auto",
                        },
                      },
                    })}
                    getItemType={(item) => {
                      if (item.__isHeader) return "header";
                      if (!item.id) return "empty";
                      if (item?.promotion_index) return "ad";
                      return "item";
                    }}
                    maintainVisibleContentPosition={{
                      autoscrollToTopThreshold: 10,
                    }}
                    {...(!isLoading &&
                      products?.length - promotionsCount <= 0 &&
                      (theme.isLargeScreen || useMobileWebView) && {
                        ListEmptyComponent: (
                          <EmptyView
                            productType={productType}
                            sectionName={sectionName}
                            isFilterVisible={isFilterVisible}
                            toggleFilterScreen={toggleFilterScreenWrapper}
                          />
                        ),
                      })}
                    ListFooterComponent={
                      <>
                        {isFetchingNextPage || isLoading
                          ? useSkeleton &&
                            !(isIframeAutoHeight && isFetchingNextPage) &&
                            (selectedViewType ===
                              SearchResultsAndFavoritesEnum.LIST ||
                            selectedViewType ===
                              SearchResultsAndFavoritesEnum.LIST_EXPANDED ? (
                              <ListViewSkeleton
                                rows={fullProducts?.length > 0 ? 3 : 20}
                                fromSearchResult={true}
                                useMobileWebView={useMobileWebView}
                              />
                            ) : (
                              <GridViewSkeleton
                                columns={numColumns}
                                rows={fullProducts?.length > 0 ? 1 : 2}
                                isFilterVisible={isFilterVisible}
                              />
                            ))
                          : null}
                        {(theme.isTablet || useMobileWebView) &&
                          (!isIframeAutoHeight ||
                            selectedItems?.length > 0) && (
                            <Spacer
                              size={
                                (isIframeAutoHeight ? 0 : SpaceType.Size_100) +
                                (selectedItems?.length > 0 ? 56 : 0)
                              }
                            />
                          )}
                      </>
                    }
                    scrollEventThrottle={
                      theme.isLargeScreen || useMobileWebView ? 128 : 16
                    }
                    bounces={false}
                    horizontal={false}
                  />
                )}
              </HorizontalScrollWrapper>

              {isIframeAutoHeight && hasNextPage ? (
                <View
                  nativeID="iframe-load-more"
                  style={{
                    width: "100%",
                    maxWidth: frameWidth,
                    alignSelf: "flex-start",
                  }}
                >
                  <LoadMoreButton
                    productType={productType}
                    onPress={handleLoadMore}
                    text={t("favoritesList.loadMore.text", "Load more")}
                    isLoading={isFetchingNextPage}
                  />
                </View>
              ) : null}

              {isFavoritesLoading || isBulkActionsLoading ? (
                <Loader backgroundColor={theme["core-02"]} opacity={0.8} />
              ) : null}
              <SearchResultModals
                showModal={showModal}
                showBMHModal={showBMHModal}
                showCertificate={showCertificate}
                setShowModal={setShowModal}
                setShowBMHModal={setShowBMHModal}
                setShowCertificate={setShowCertificate}
                setRemoveFavoritesItem={setRemoveFavoritesItem}
                setSingleItem={setSingleItem}
                setCertificateURL={setCertificateURL}
                setOpenBulkDialog={setOpenBulkDialog}
                selectedItems={selectedItems}
                itemDetails={itemDetails}
                singleItem={singleItem}
                certificateURL={certificateURL}
                productType={productType}
                priceMode={priceMode}
                removeFavoritesItem={removeFavoritesItem}
                isGuest={isGuest}
                favoriteRemoved={favoriteRemoved}
                bulkActionCallback={bulkActionCallback}
                sendEmailExcelData={sendEmailExcelData}
                DownloadCSVData={DownloadCSVData}
                addCollectionSuccess={addCollectionSuccess}
                bulkActions={bulkActions}
                t={t}
                isMyInventory={isMyInventory}
                totalItems={totalItems ?? 0}
              />
            </LayoutContainer>
          </View>
        )}
      </SearchResultProvider>

      {/* {products?.length > 0 && !theme.isLargeScreen && !isIFrame() ? (
        <AnimatedFloatingButton
          title={`${t("searchResult.bulkActions.button.text")} ${
            selectedItems.length ? `(${selectedItems.length})` : ""
          }`}
          buttonWidth={180}
          buttonTextHeight={48}
          buttonTextWidth={120}
          buttonTextMarginStart={10}
          floatingButtonClick={() => setOpenBulkDialog(true)}
          bottomMargin={Platform.OS === "ios" ? 110 : 90}
        />
      ) : null} */}
    </ScopedCopilotProvider>
  );
};

export default memo(SearchResultScreen);
